import { clsx } from 'clsx'
import { PropertyValueMap, unsafeCSS } from 'lit'
import { property, state } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { PopupPlacement } from '../../common/types'
import { DdsElement } from '../../internal/dds-hu-element'
import DapDSPopup from '../popup/popup.component'
import styles from './tooltip.scss?inline'
export type TooltipMode = 'tooltip' | 'toggle'

/**
 * The distance in pixels between the tooltip and the trigger element.
 */
const TOOLTIP_OFFSET = 8

/**
 * `dap-ds-tooltip`
 * @summary A tooltip is a container for tooltip content.
 * @element dap-ds-tooltip
 * @title - Tooltip
 *
 * @slot - The content of the tooltip.
 * @slot trigger - The trigger element of the tooltip.
 *
 * @csspart base - The main tooltip container.
 * @csspart trigger - The trigger element of the tooltip.
 * @csspart popup - The popup of the tooltip.
 * @csspart arrow - The arrow of the tooltip.
 *
 * @cssproperty --dds-tooltip-max-width - The maximum width of the tooltip before its content will wrap (default: 14rem)
 * @cssproperty --dds-tooltip-padding - The padding inside the tooltip (default: var(--dds-spacing-400))
 * @cssproperty --dds-tooltip-border-color - The border color of the tooltip (default: var(--dds-border-neutral-subtle))
 * @cssproperty --dds-tooltip-bg-color - The background color of the tooltip (default: var(--dds-background-neutral-base))
 * @cssproperty --dds-tooltip-text-color - The text color of the tooltip (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-tooltip-font-size - The font size of the tooltip text (default: var(--dds-font-sm))
 * @cssproperty --dds-tooltip-box-shadow - The box shadow of the tooltip (default: 0 8px 12px -2px rgb(0 0 0 / 8%), 0 2px 6px -2px rgb(0 0 0 / 6%))
 */
export default class DapDSTooltip extends DdsElement {
  static tagName = 'dap-ds-tooltip'

  static dependencies = {
    'dap-ds-popup': DapDSPopup,
  }

  static readonly styles = unsafeCSS(styles)

  /** The content of the tooltip, supporting text only. */
  @property() content: string

  /** The position of the tooltip around the trigger element.
   * @type { 'top' | 'right' | 'bottom' | 'left' }
   */
  @property({ reflect: true }) placement: PopupPlacement = 'bottom'

  /** Sets the tooltip to opened by default (will still be closed on closing events). */
  @property({ type: Boolean, reflect: true }) opened? = false

  /** Sets the tooltip to toggle mode.
   * @type { 'tooltip' | 'toggle' }
   */
  @property() mode?: TooltipMode = 'tooltip'

  /** Hides the arrow of the tooltip. */
  @property({ type: Boolean, reflect: true }) noArrow? = false

  /** The floating strategy of the tooltip. Default is `absolute`. Can be `absolute` or `fixed`.
   * @type { 'absolute' | 'fixed' }
   */
  @property({ reflect: true })
  floatingStrategy: 'absolute' | 'fixed' = 'absolute'

  /** Whether to keep popup open when trigger leaves viewport. */
  @property({ type: Boolean }) disableViewportAutoClose: boolean = false

  private _trigger: string = 'hover focus'

  /** Sets custom trigger event (hover, focus, click). Default is `hover focus`. */
  @property()
  set trigger(value: string) {
    this._trigger = value
  }
  get trigger() {
    if (!this.mode) {
      return 'hover focus'
    }

    if (this.mode === 'toggle') {
      return 'click'
    }

    return this._trigger
  }

  /** @ignore */
  @state() mouseOverTooltip: boolean = false

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener('blur', this.handleBlur, true)
    this.addEventListener('focus', this.handleFocus, true)
    this.addEventListener('click', this.handleClick)
    this.addEventListener('mouseover', this.handleMouseOver)
    this.addEventListener('mouseout', this.handleMouseOut)
  }

  disconnectedCallback() {
    this.removeEventListener('blur', this.handleBlur, true)
    this.removeEventListener('focus', this.handleFocus, true)
    this.removeEventListener('click', this.handleClick)
    this.removeEventListener('mouseover', this.handleMouseOver)
    this.removeEventListener('mouseout', this.handleMouseOut)
    super.disconnectedCallback()
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    this.setAttribute('role', 'status')
  }

  async showTooltip() {
    if (this.opened) {
      return
    }

    this.opened = true

    const trigger = this.querySelector('[slot="trigger"]')
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true')
    }
  }

  async hideTooltip() {
    if (!this.opened) {
      return
    }

    this.opened = false

    const trigger = this.querySelector('[slot="trigger"]')
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false')
    }
  }

  private readonly handleBlur = () => {
    if (this.hasTrigger('focus')) {
      this.hideTooltip()
    }
  }

  private readonly handleClick = () => {
    if (this.hasTrigger('click')) {
      this.opened && !this.hasTrigger('focus')
        ? this.hideTooltip()
        : this.showTooltip()
    }
  }

  private readonly handleFocus = () => {
    if (this.hasTrigger('focus')) {
      this.showTooltip()
    }
  }

  private readonly handleMouseOver = () => {
    if (this.hasTrigger('hover')) {
      this.showTooltip()
    }
  }

  private readonly handleMouseOut = () => {
    if (!this.mouseOverTooltip) {
      if (this.hasTrigger('hover')) {
        this.hideTooltip()
      }
    }
  }

  private readonly hasTrigger = (triggerType: string) => {
    const triggers = this.trigger.split(' ')
    return triggers.includes(triggerType)
  }

  render() {
    return html`
      <dap-ds-popup
        part="base"
        role="tooltip"
        id="tooltip"
        exportparts="
          popup:base__popup,
          arrow:base__arrow
        "
        class=${clsx('tooltip', {
          'tooltip--open': this.opened,
          'tooltip--tooltip': this.mode === 'tooltip',
          'tooltip--toggle': this.mode === 'toggle',
        })}
        ?opened=${this.opened}
        placement=${this.placement}
        offset=${TOOLTIP_OFFSET}
        floatingStrategy=${this.floatingStrategy}
        ?disableViewportAutoClose=${this.disableViewportAutoClose}
        ?hasArrow=${!this.noArrow}
        @dds-close=${this.hideTooltip}
        @dds-closed=${this.hideTooltip}
        @dds-opened=${this.showTooltip}>
        <slot
          part="trigger"
          name="trigger"
          slot="trigger"
          aria-describedby="tooltip"></slot>
        ${this.content ? html`${this.content}` : html`<slot></slot>`}
      </dap-ds-popup>
    `
  }
}
